import React, { useCallback, useEffect, useRef, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import { useDispatch, useSelector } from 'react-redux'
import CloseIcon from 'components/atoms/Svgs/Close'
import FilterIcon from 'components/atoms/Icons/Controls/Filter'
import Switch from 'components/atoms/Switch/Switch'
import Button from 'components/atoms/Button/Button'
import { animationConfig } from 'views/constants'
import { ROUTES } from 'constants/routes'
import { userApiEvents, userApiSelectors } from 'modules/api/user'
import { agencyApiEvents } from 'modules/api/agency'
// the state entry point, not the module barrel: the barrel pulls in the module's components, which import views/components
import { bookSegmentsSelectors } from 'modules/bookSegments/state'
import styles from './SegmentsDrawer.module.scss'

const SEGMENTS_DESCRIPTION =
  'Segments group leases into collections like portfolios or teams, and act as a filter in the Leases and Invoices sections.'
const MANAGED_BY_OWNERS = 'Segments are managed by account owners.'

/** Batches quick successive toggles into a single token refresh. */
const APPLY_DEBOUNCE_MS = 300

type TProps = {
  open: boolean
  onClose: () => void
  redirect: (route: string) => void
}

const SegmentsDrawer = ({ open, onClose, redirect }: TProps): React.ReactElement => {
  const dispatch = useDispatch()
  const currentSegments = useSelector(userApiSelectors.getCurrentSegments)
  const defaultSegment = useSelector(bookSegmentsSelectors.getDefaultSegmentId)

  useEffect(() => {
    if (open) {
      dispatch(agencyApiEvents.getSegments_request())
      // refresh token to potentially get new token memberships
      dispatch(userApiEvents.refreshToken_request({ selectedSegments: currentSegments }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch])

  const style = useSpring({
    reverse: !open,
    config: animationConfig,
    to: { transform: 'translateX(0)' },
    from: { transform: 'translateX(110%)' },
  })

  const availableSegments = useSelector(bookSegmentsSelectors.getAvailableSegments)
  const appliedSegmentIds = useSelector(userApiSelectors.getCurrentSegmentsWithoutDefault)
  const isOwnerRole = useSelector(userApiSelectors.isOwnerRole)

  // Toggles update optimistically; the store catches up once the refreshed token lands.
  const [selected, setSelected] = useState<string[]>(appliedSegmentIds)
  useEffect(() => setSelected(appliedSegmentIds), [appliedSegmentIds])

  const applyTimer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(applyTimer.current), [])

  const applySelection = useCallback(
    (next: string[], immediate = false) => {
      setSelected(next)
      clearTimeout(applyTimer.current)
      // No segments toggled on means the account falls back to its default segment.
      const apply = () =>
        dispatch(userApiEvents.refreshToken_request({ selectedSegments: next.length > 0 ? next : [defaultSegment] }))
      if (immediate) {
        apply()
      } else {
        applyTimer.current = setTimeout(apply, APPLY_DEBOUNCE_MS)
      }
    },
    [dispatch, defaultSegment],
  )

  const handleToggle = (id: string) =>
    applySelection(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id])

  const goTo = (route: string) => {
    redirect(route)
    onClose()
  }
  const goToManageSegments = () => goTo(ROUTES.segments)
  const goToCreateSegment = () => goTo(ROUTES.segmentView.replace(':id', 'new'))

  const hasSegments = availableSegments.length > 0

  return (
    <animated.div id="rbn-segments" className={styles.root} style={style}>
      <button className={styles.close} onClick={onClose} aria-label="Close segments">
        <CloseIcon />
      </button>
      <header>
        <h2>
          <FilterIcon />
          Segments
        </h2>
        {hasSegments && (
          <p>
            {SEGMENTS_DESCRIPTION}{' '}
            {isOwnerRole ? (
              <button className={styles.link} onClick={goToManageSegments}>
                Manage segments
              </button>
            ) : (
              MANAGED_BY_OWNERS
            )}
          </p>
        )}
      </header>

      {hasSegments ? (
        <>
          <div className={styles.summary}>
            <span>
              {selected.length} {selected.length === 1 ? 'segment' : 'segments'} on
            </span>
            {selected.length > 0 && (
              <Button className={styles['deselect-all']} size="xs" ghost onClick={() => applySelection([], true)}>
                Deselect all
              </Button>
            )}
          </div>
          <ul className={styles.segments}>
            {availableSegments.map(segment => (
              <li key={segment.id}>
                <Switch
                  id={`segment-toggle-${segment.id}`}
                  label={segment.name}
                  checked={selected.includes(segment.id)}
                  onChange={() => handleToggle(segment.id)}
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className={styles.empty}>
          <FilterIcon className={styles['empty-icon']} />
          <h2>No segments have been set up</h2>
          <p>
            {SEGMENTS_DESCRIPTION}
            {!isOwnerRole && ` ${MANAGED_BY_OWNERS}`}
          </p>
          {isOwnerRole && <Button onClick={goToCreateSegment}>Create segment</Button>}
        </div>
      )}
    </animated.div>
  )
}

export default SegmentsDrawer
